import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import AtsApplicationsAlreadyImportedError from '@/Utils/HTTP/Errors/AtsApplicationsAlreadyImportedError';
import AtsConnectionNotReadyError from '@/Utils/HTTP/Errors/AtsConnectionNotReadyError';
import NoImportableAtsApplicationsError from '@/Utils/HTTP/Errors/NoImportableAtsApplicationsError';
import http from '@/Utils/HTTP/NewHttpUtils';
import UIStrings from '@/Utils/UIStrings';

/**
 * Acknowledgement that the import was accepted. The work itself runs in the background, so nothing
 * here describes a result — {@link useAtsImportJob} is what reports progress.
 */
export interface AtsImportStartedResponse {
    /** Handle for the queued run. */
    importJobId: string;
    /** How many applications the ATS returned, importable or not. */
    totalReturned: number;
    /** How many of them are importable. */
    wouldImportCount: number;
}

export const buildImportAtsApplicationsPath = (recruitmentId: string) =>
    `recruitments/${recruitmentId}/import-ats-applications`;

const resolveImportErrorMessage = (error: unknown): string => {
    if (error instanceof NoImportableAtsApplicationsError) {
        return UIStrings.AtsNoImportableApplications;
    }

    if (error instanceof AtsApplicationsAlreadyImportedError) {
        return UIStrings.AtsApplicationsAlreadyImported;
    }

    if (error instanceof AtsConnectionNotReadyError) {
        return UIStrings.AtsSyncNotReadyToast;
    }

    return UIStrings.AtsApplicationsLoadFailed;
};

/**
 * Queues every application on an ATS job for import. One call covers the whole job: pagination is
 * the backend's business, so no cursor is threaded through the UI.
 */
const useImportAtsApplications = (recruitmentId: string) => {
    const { getAccessTokenSilently } = useAuth0();

    return useSWRMutation(
        buildImportAtsApplicationsPath(recruitmentId),
        async (path: string, { arg: atsJobId }: { arg: string }) => {
            const response = await http.post<AtsImportStartedResponse>({
                path,
                body: { atsJobId },
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        },
        {
            onError: (error) => {
                // Overrides the global failure toaster, which would otherwise stack a generic
                // "something went wrong" on top of the reason and invite a pointless retry.
                toast.error(resolveImportErrorMessage(error));
            },

            // The toast above is the whole error handling, so trigger resolves to undefined instead of
            // rejecting into a caller that has nothing left to do with it.
            throwOnError: false,
        },
    );
};

export default useImportAtsApplications;
